import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { ComponentProps, forwardRef, HTMLAttributes, ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import logCommunityHeatmapsCTAViewed from '@alltrails/amplitude/events/logCommunityHeatmapsCTAViewed';
import logMapLayerDrawerTabTapped from '@alltrails/amplitude/events/logMapLayerDrawerTabTapped';
import logTrailConditionsCTAViewed from '@alltrails/amplitude/events/logTrailConditionsCTAViewed';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import Typography from '@alltrails/core/components/Typography';
import useScroll from '@alltrails/core/hooks/useScroll';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Close from '@alltrails/denali/icons/Close';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import Modal from '@alltrails/modal/components/Modal/Modal';
import { useModalAriaLabelledBy } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import ModalStep from '@alltrails/modal/components/Modal/ModalStep';
import MultiStepModal from '@alltrails/modal/components/Modal/MultiStepModal';
import useMultiStepModal from '@alltrails/modal/hooks/useMultiStepModal';
import { RootState, useAppSelector } from '../../redux';
import { TwoTabLayerType } from '../../types/LayerMenu';
import { DynamicStyleId, StaticStyleId, StyleId } from '../../types/Styles';
import ExtraMenuItem from '../ExtraMenuItem';
import GridBaseMapMenuItem from '../LayerMenuItem/GridBaseMapMenuItem';
import CustomURLModal from './CustomURLModal';
import filterHiddenStyles from './filterHiddenStyles';
import GridLayerCards from './GridLayerCards';
import styles from './GridLayerMenu.module.scss';
import HeatmapPrivacyModalContent from './HeatmapPrivacyModalContent';
import LayerControl from './LayerControl';
import { baseMapStyles, conditionsStyles, extraStyles, heatmapStyles, layerOverlayStyles, terrainOverlayStyles } from './layerMenuStyles';

// https://www.figma.com/design/P20agwni00K1tSybBZL4oz/Overlays-Working?node-id=842-151736&m=dev

function MobileLayerMenuLabel({ activeModal }: { activeModal: 'cards' | 'heatmap-privacy' }) {
  const id = useModalAriaLabelledBy();
  return (
    <div id={id} className={styles.visuallyHidden}>
      {activeModal === 'cards' ? <FormattedMessage defaultMessage="Layer menu" /> : <FormattedMessage defaultMessage="About heatmaps" />}
    </div>
  );
}

/**
 * Order of style ids within each grouped section.
 * Items not listed here aren't rendered in the grouped layout.
 */
type DetailStyleGroupId = 'community-content' | 'on-trail-details' | 'nearby-trails-routes';
export const detailStyleGroups: { id: DetailStyleGroupId; styleIds: (StaticStyleId | DynamicStyleId)[] }[] = [
  { id: 'community-content', styleIds: ['userWaypoints', 'communityWaypoints', 'communityActivity'] },
  { id: 'on-trail-details', styleIds: ['photos', 'surfaceType', 'distanceMarkers'] },
  {
    id: 'nearby-trails-routes',
    styleIds: ['savedRoutePolyline', 'nearbyTrails', 'waymarkedHiking', 'waymarkedCycling', 'waymarkedMTB']
  }
];

type Props = {
  allowedStyleIds?: StyleId[];
  isOpen: boolean;
  onCloseRequest: ComponentProps<typeof Modal>['onCloseRequest'];
  desktopModalProps: HTMLAttributes<HTMLDivElement>;
  mapLocation?: MapLocation;
  pauseFocusTrap?: boolean;
} & Pick<
  ComponentProps<typeof GridBaseMapMenuItem>,
  'disableAnalytics' | 'handleFailedEntitlementCheck' | 'handleRoadblock' | 'onMapBaseLayerTapped' | 'onMapOverlayTapped'
>;

const GridLayerMenu = forwardRef<HTMLDivElement, Props>(
  (
    {
      allowedStyleIds,
      isOpen,
      onCloseRequest,
      onMapBaseLayerTapped,
      onMapOverlayTapped,
      disableAnalytics,
      handleFailedEntitlementCheck,
      handleRoadblock,
      desktopModalProps,
      mapLocation,
      pauseFocusTrap
    },
    desktopModalRef
  ) => {
    const intl = useIntl();
    const isMobile = useIsMobileSizedScreen();
    const scrollContainer = useRef<HTMLDivElement>(null);
    const { hasPermission } = useAuthorization();
    const isAdmin = hasPermission({ permission: 'trails:manage' });
    const isPLP = hasPermission({ permission: 'public_lands:manage' });
    const { canScrollUp, updateScroll } = useScroll(scrollContainer);
    const { is3dActive, supportedDynamicStyleIds } = useAppSelector((state: RootState) => ({
      is3dActive: state.map.is3dActive,
      supportedDynamicStyleIds: state.map.supportedDynamicStyleIds,
      dynamicStyleIds: state.map.dynamicStyleIds
    }));
    const [adminStyleSourceUrlId, setAdminStyleSourceUrlId] = useState<StyleId | null>('adminBasemap');
    const { activeModal, goBack, navigateTo } = useMultiStepModal<'cards' | 'heatmap-privacy'>('cards');
    const [isCustomUrlModalOpen, setIsCustomUrlModalOpen] = useState(false);
    const experiments = useExperiments();
    const isPhotoHeatmapEnabled = experiments['web-tmt-rm-photos-overlay']?.value === 'on';
    const isRecordingOverlaysEnabled = experiments['web-tmt-rm-recording-overlays']?.value === 'on';
    const desktopAriaLabelElementId = useId();
    const loggedOverlayIds = useRef(new Set<StyleId>());

    const renderBaseMapCards = useCallback(
      (ids: StyleId[]): ReactElement[] | null => {
        return ids.length
          ? ids.map(id => (
              <GridBaseMapMenuItem
                key={`card-${id}`}
                styleId={id}
                onMapBaseLayerTapped={onMapBaseLayerTapped}
                onMapOverlayTapped={onMapOverlayTapped}
                disableAnalytics={disableAnalytics}
                onUrlButtonClick={() => setIsCustomUrlModalOpen(true)}
                handleFailedEntitlementCheck={handleFailedEntitlementCheck}
                handleRoadblock={handleRoadblock}
              />
            ))
          : null;
      },
      [onMapBaseLayerTapped, onMapOverlayTapped, disableAnalytics, handleFailedEntitlementCheck, handleRoadblock]
    );

    const renderOverlayCards = useCallback(
      (ids: StyleId[]): ReactElement[] | null => {
        return ids.length
          ? ids.map(id => (
              <GridBaseMapMenuItem
                key={`card-${id}`}
                styleId={id}
                onMapBaseLayerTapped={onMapBaseLayerTapped}
                onMapOverlayTapped={onMapOverlayTapped}
                disableAnalytics={disableAnalytics}
                onUrlButtonClick={() => setIsCustomUrlModalOpen(true)}
                handleFailedEntitlementCheck={handleFailedEntitlementCheck}
                handleRoadblock={handleRoadblock}
                loggedOverlayIds={loggedOverlayIds}
              />
            ))
          : null;
      },
      [onMapBaseLayerTapped, onMapOverlayTapped, disableAnalytics, handleFailedEntitlementCheck, handleRoadblock]
    );

    const renderDetailCards = useCallback(
      (ids: StyleId[]) => {
        return ids.map(id => (
          <ExtraMenuItem
            key={`card-${id}`}
            styleId={id}
            onMapBaseLayerTapped={onMapBaseLayerTapped}
            onMapOverlayTapped={onMapOverlayTapped}
            disableAnalytics={disableAnalytics}
            onUrlButtonClick={() => setIsCustomUrlModalOpen(true)}
            handleFailedEntitlementCheck={handleFailedEntitlementCheck}
            handleRoadblock={handleRoadblock}
            loggedOverlayIds={loggedOverlayIds}
          />
        ));
      },
      [onMapBaseLayerTapped, onMapOverlayTapped, disableAnalytics, handleFailedEntitlementCheck, handleRoadblock]
    );

    const filterVisibleStyles = useCallback(
      (ids: StyleId[]) => filterHiddenStyles({ allowedStyleIds, ids, isAdmin, isPLP, is3dActive, supportedDynamicStyleIds }),
      [allowedStyleIds, is3dActive, isAdmin, isPLP, supportedDynamicStyleIds]
    );

    const baseMapCards = useMemo(() => renderBaseMapCards(filterVisibleStyles(baseMapStyles)), [filterVisibleStyles, renderBaseMapCards]);

    const visibleHeatmapIds = useMemo(() => filterVisibleStyles(heatmapStyles), [filterVisibleStyles]);

    const heatmapCards = useMemo(() => renderOverlayCards(visibleHeatmapIds), [renderOverlayCards, visibleHeatmapIds]);

    const hasCommunityHeatmapCard = visibleHeatmapIds.includes('newHeatmap');

    const conditionsCards = useMemo(() => {
      return renderOverlayCards(filterVisibleStyles(conditionsStyles));
    }, [filterVisibleStyles, renderOverlayCards]);

    const layerOverlayCards = useMemo(() => {
      return renderOverlayCards(filterVisibleStyles(layerOverlayStyles));
    }, [filterVisibleStyles, renderOverlayCards]);

    const terrainCards = useMemo(() => {
      return renderOverlayCards(filterVisibleStyles(terrainOverlayStyles));
    }, [filterVisibleStyles, renderOverlayCards]);

    const detailCards = useMemo(() => {
      const ids = ['communityActivity', ...extraStyles].filter(id => {
        if (id === 'recordingStart' || id === 'recordingEnd') {
          return isRecordingOverlaysEnabled;
        }
        if (id === 'photoHeatmap') {
          return isPhotoHeatmapEnabled;
        }
        return true;
      }) as (StaticStyleId | DynamicStyleId)[];
      return renderDetailCards(filterVisibleStyles(ids));
    }, [filterVisibleStyles, renderDetailCards, isPhotoHeatmapEnabled, isRecordingOverlaysEnabled]);

    const detailsGroups = useMemo(() => {
      if (isAdmin || isPLP) {
        return undefined;
      }

      return detailStyleGroups
        .map(group => {
          const visibleIds = filterVisibleStyles(group.styleIds);
          let groupTitle = '';
          if (group.id === 'on-trail-details') {
            groupTitle = intl.formatMessage({ defaultMessage: 'On-trail details' });
          } else if (group.id === 'community-content') {
            groupTitle = intl.formatMessage({ defaultMessage: 'Community content' });
          } else if (group.id === 'nearby-trails-routes') {
            groupTitle = intl.formatMessage({ defaultMessage: 'Nearby trails & routes' });
          }

          return {
            id: group.id,
            title: groupTitle,
            items: renderDetailCards(visibleIds)
          };
        })
        .filter(group => group.items.length > 0);
    }, [filterVisibleStyles, intl, isAdmin, isPLP, renderDetailCards]);

    const hasDetails = detailsGroups ? detailsGroups.length > 0 : detailCards.length > 0;
    const hasLayers = !!(baseMapCards || heatmapCards || conditionsCards || layerOverlayCards || terrainCards);
    const options = useMemo(
      () => [
        ...(hasLayers ? [{ id: 'layers' as TwoTabLayerType, label: intl.formatMessage({ defaultMessage: 'Layers' }) }] : []),
        ...(hasDetails ? [{ id: 'details' as TwoTabLayerType, label: intl.formatMessage({ defaultMessage: 'Details' }) }] : [])
      ],
      [intl, hasDetails, hasLayers]
    );
    const [selectedLayerType, setSelectedLayerType] = useState<TwoTabLayerType>('layers');
    const layerType = options.some(option => option.id === selectedLayerType) ? selectedLayerType : (options[0]?.id ?? 'layers');
    const hasLoggedOverlayViewEvents = useRef(false);

    useEffect(() => {
      if (!disableAnalytics && mapLocation && layerType === 'layers' && !hasLoggedOverlayViewEvents.current) {
        if (hasCommunityHeatmapCard) {
          logCommunityHeatmapsCTAViewed({ map_location: mapLocation });
        }
        if (conditionsCards) {
          logTrailConditionsCTAViewed({ map_location: mapLocation });
        }
        hasLoggedOverlayViewEvents.current = true;
      }
    }, [conditionsCards, disableAnalytics, hasCommunityHeatmapCard, layerType, mapLocation]);

    const onLayerTypeChange = useCallback(
      (newLayerType: TwoTabLayerType) => {
        setSelectedLayerType(newLayerType);
        setAdminStyleSourceUrlId(newLayerType === 'layers' ? 'adminBasemap' : newLayerType === 'details' ? 'adminOverlay' : null);
        if (!disableAnalytics && mapLocation) {
          logMapLayerDrawerTabTapped({
            map_location: mapLocation,
            to_map_tab: newLayerType === 'layers' ? 'base map' : 'extras'
          });
        }
        if (scrollContainer.current) {
          scrollContainer.current.scrollTo?.({ top: 0 });
        }
      },
      [disableAnalytics, mapLocation]
    );

    return (
      <>
        {isMobile ? (
          <MultiStepModal
            isOpen={isOpen}
            modalClassName={styles.mobileMenuModal}
            isPaused={pauseFocusTrap}
            initialFocus={false}
            onBackRequest={activeModal === 'cards' ? undefined : goBack}
            onCloseRequest={onCloseRequest}
          >
            <MobileLayerMenuLabel activeModal={activeModal} />
            {activeModal === 'cards' ? (
              <ModalStep onCloseRequest={onCloseRequest} contentsClassName={styles.modalContent}>
                <GridLayerCards
                  layerType={layerType}
                  baseMapCards={baseMapCards}
                  conditionsCards={conditionsCards}
                  detailCards={detailCards}
                  detailsGroups={detailsGroups}
                  disableAnalytics={disableAnalytics}
                  heatmapCards={heatmapCards}
                  layerControl={<LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />}
                  onHeatmapPrivacyIconClick={() => navigateTo('heatmap-privacy')}
                  layerOverlayCards={layerOverlayCards}
                  terrainCards={terrainCards}
                />
              </ModalStep>
            ) : (
              <ModalStep
                onCloseRequest={onCloseRequest}
                title={<FormattedMessage defaultMessage="About heatmaps" />}
                contentsClassName={styles.mobilePrivacyContent}
                buttons={{
                  primaryButton: {
                    onClick: () => navigateTo('cards'),
                    text: <FormattedMessage defaultMessage="Got it" />
                  }
                }}
              >
                <div className={styles.heatmapPrivacyContent}>
                  <HeatmapPrivacyModalContent />
                </div>
              </ModalStep>
            )}
          </MultiStepModal>
        ) : (
          <ModalPortal>
            <FocusTrap
              active={isOpen}
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: onCloseRequest,
                // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
                fallbackFocus: '#layer-menu-container'
              }}
              paused={pauseFocusTrap}
            >
              <div
                ref={desktopModalRef}
                className={styles.layerMenuModal}
                aria-labelledby={desktopAriaLabelElementId}
                role="dialog"
                id="layer-menu-container"
                data-at-theme-dimensions="default"
                {...desktopModalProps}
              >
                <div className={styles.titleContainer} id={desktopAriaLabelElementId}>
                  <Typography component="h2" className={styles.title} variant="heading300">
                    <FormattedMessage defaultMessage="Customize map" />
                  </Typography>
                  <IconButton
                    icon={{ Component: Close }}
                    onClick={onCloseRequest}
                    size="sm"
                    title={intl.formatMessage({ defaultMessage: 'Close' })}
                    variant="secondary"
                  />
                </div>
                {options?.length > 1 && (
                  <div className={classNames(styles.header, { [styles.bottomBorder]: canScrollUp })}>
                    <LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />
                  </div>
                )}
                <div className={styles.scrollContainer} onScroll={updateScroll} ref={scrollContainer} id="modal-scroll-container">
                  <div
                    className={classNames(styles.modalContent, {
                      [styles.morePadding]: options.length <= 1
                    })}
                  >
                    <GridLayerCards
                      layerType={layerType}
                      baseMapCards={baseMapCards}
                      conditionsCards={conditionsCards}
                      detailCards={detailCards}
                      detailsGroups={detailsGroups}
                      disableAnalytics={disableAnalytics}
                      heatmapCards={heatmapCards}
                      layerControl={<LayerControl layerType={layerType} options={options} onLayerTypeChange={onLayerTypeChange} />}
                      onHeatmapPrivacyIconClick={() => navigateTo('heatmap-privacy')}
                      layerOverlayCards={layerOverlayCards}
                      terrainCards={terrainCards}
                    />
                  </div>
                </div>
              </div>
            </FocusTrap>
          </ModalPortal>
        )}
        {isCustomUrlModalOpen && adminStyleSourceUrlId && (
          <CustomURLModal
            isOpen={isCustomUrlModalOpen}
            adminStyleSourceUrlId={adminStyleSourceUrlId}
            onClose={() => setIsCustomUrlModalOpen(false)}
          />
        )}
      </>
    );
  }
);

export default GridLayerMenu;
